import { useEffect, useRef, useState } from "react"

import acc from "accounting"
import { App, Divider, Flex, Layout, Space, Spin, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import InputNumberComponent from "../../../Components/Input/InputNumber"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { subStepsStorage } from "../../../Utils/onboarding/setStatusSubSteps"
import AppUtils, { createAccountStepPath, onboardingStepPath, useQuery } from "../../../Utils/utils"
import { useShopifyOnlyUser } from "../../Balance/hooks/useShopifyOnlyUser"
import Creators from "../../Balance/reducer"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import { updateProfile } from "../../LoginPage/redux/auth.actions"
import { useBalanceTopUpEvent } from "../../PersonalSettings/hooks/useBalanceTopUpEvent"
import { getBusinessAccounts, getUserInfo } from "../../User/redux/user.thunks"
import { updateCompletedStep } from "../../User/services"
import MobileCtaButtons from "../PaymentConnectPage/components/MobileCtaButtons"
import PaymentMethods from "./components/PaymentMethods"

import "./styles.less"

const { Title, Text, Link } = Typography

const onboardingBalance = [100, 200, 1000, 5000, 15000]

const OnboardingTopUp = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { isMobile, theme } = useSelector((state) => state.nav)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const isCreatedByInvitation = useSelector(
    (state) => state.auth.userInfo?.is_created_by_invitation
  )

  const { isShopifyOnlyUser, isShopifyDefaultProcessor } = useShopifyOnlyUser()

  const { fetching, balance, saveFetching, saveResult, addFundsLoading } = useSelector(
    (state) => state.balance
  )

  const { search, pathname } = useLocation()
  const navigate = useNavigate()
  const query = useQuery(search)
  const paymentMethodsRef = useRef(null)
  const first_topup_status = query.get("first_topup_status")
  const valueFromQuery = query.get("value")

  const [value, setValue] = useState(null)
  const submittedAmount = Math.max(Number(value) || 0, 100)

  const isNewBusiness = pathname?.includes("/create-account")

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    dispatch(getBusinessAccounts())
    dispatch(Creators.resetAddFundsLoading())
    dispatch(getUserInfo())
    dispatch(Creators.getBalanceRequest())
    //TODO: Remove sub step status for onboarding
    subStepsStorage.remove()
  }, [])

  const showPaymentWarning = (message) => {
    modal.confirm({
      ...modalConfig("error", 442),
      className: "ob-clear-bg cdrn",
      okButtonProps: { size: "large", style: { width: "auto" } },
      centered: !isMobile,
      title: t("Payment Failed"),
      content:
        message ||
        t(
          "We couldn’t complete your top-up. Please check that your card details are correct and ensure sufficient funds are available. You can also try again or use a different card."
        ),
      okText: t("Try again"),
      cancelText: t("Cancel"),
      cancelButtonProps: { width: 70, height: 40 }
    })
  }
  const showPaymentSuccess = (nextBalanceAmount, redirect_url) => {
    modal.warning({
      ...modalConfig("success"),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,
      title: t("Top up successful"),
      content: t(
        `You have successfully topped up your account. Your new balance is $${acc.formatNumber(
          nextBalanceAmount || 0,
          2,
          ",",
          "."
        )}`
      ),
      okText: t("Explore Chargeback"),
      okButtonProps: {
        width: 154,
        height: 40
      },
      onOk() {
        localStorage.removeItem("onboarding_finish_notification")
        AppUtils.goLink(redirect_url)
      }
    })
  }

  const stripeTopUpSuccess = (data) => {
    if (data?.message) {
      showPaymentWarning(data?.message?.detail || data?.message)
      return
    }
    dispatch(updateProfile({ new_onboarding_finished: true }))
    if (isCreatedByInvitation) {
      updateCompletedStep({
        step: 6,
        business_account_id: businessAccount?.id
      })
    }
    dispatch(getUserInfo())
    showPaymentSuccess(Number(balance?.amount ?? 0) + submittedAmount, data?.redirect_url)
  }

  const shopifyTopUpSuccess = () => {
    dispatch(updateProfile({ new_onboarding_finished: true }))
    if (isCreatedByInvitation) {
      updateCompletedStep({
        step: 6,
        business_account_id: businessAccount?.id
      })
    }
    dispatch(getUserInfo())
    showPaymentSuccess(Number(valueFromQuery ?? 0), process.env.REACT_APP_BASE_URI + "/dashboard")
  }

  useEffect(() => {
    if (first_topup_status === "success") {
      shopifyTopUpSuccess()
    }
  }, [first_topup_status])

  const onSuccess = (data) => {
    if (data?.provider === "shopify") {
      localStorage.setItem("onboarding_finish_notification", "true")
      AppUtils.goLink(data?.redirect_url)
      return
    }
    localStorage.setItem("onboarding_finish_notification", "true")
    stripeTopUpSuccess(data)
  }

  const handleSubmitForm = () => {
    if (!value) return
    dispatch(
      Creators.firstTopUpBalanceRequest({
        amount: submittedAmount,
        notRedirect: true,
        onSuccess: onSuccess,
        onError: () => {
          showPaymentWarning()
        }
      })
    )
  }

  const handleAmountBlur = () => {
    if (value == null || value === "") return
    if (Number(value) < 100) setValue(100)
  }

  useBalanceTopUpEvent()

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }
  const handleBack = () => {
    if (isNewBusiness) {
      return navigate(createAccountStepPath[BA_STEPS.CARD])
    }
    navigate(onboardingStepPath[OB_STEPS.CARD])
  }

  return (
    <Layout>
      <div className="onboarding-top-up">
        <Spin size="large" spinning={saveFetching}>
          <div style={{ position: "relative" }}>
            <Flex
              gap={16}
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
              wrap="wrap"
            >
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <h2
                  level={2}
                  className="topup-title"
                  style={{
                    textAlign: "left",
                    marginBottom: 8,
                    fontSize: isMobile && 18,
                    lineHeight: isMobile && "24px"
                  }}
                >
                  {t("Top up your balance")}
                </h2>
                <div style={{ marginBottom: 26 }}>
                  <Text className="topup-subtitle" type={"secondary"}>
                    {balance?.["need_amount_validation"]
                      ? t("Enter an amount no less than $_min_", {
                          min: balance?.["min_amount"].toFixed(2)
                        })
                      : t(
                          "Choose the amount of prepaid funds to add for your Chargeback account and payment method."
                        )}
                  </Text>
                </div>
                <div className="info-note modal-info-note">
                  <Icon
                    type="circle_check_green"
                    className="info-icon"
                    style={{ marginRight: !isMobile ? 8 : 4 }}
                  />
                  <span>
                    {t("Balances are refundable.")}&nbsp;
                    <Link
                      href={
                        "https://help.chargeback.io/en/articles/12782877-what-is-balance-billing"
                      }
                      target="_blank"
                      style={{ cursor: "pointer" }}
                      className="link"
                    >
                      {t("See this page for limits.")}
                    </Link>
                  </span>
                </div>
              </Space>
            </Flex>
          </div>

          <Flex vertical gap={8} style={{ margin: "24px 0 10px" }}>
            <Text strong>{t("Choose amount")}</Text>
            <InputNumberComponent
              placeholder={"0.00"}
              value={value}
              onChange={setValue}
              onBlur={handleAmountBlur}
              min={0}
              step={1}
              precision={2}
              style={{ width: "100%" }}
            />
          </Flex>
          <div className="info-tags">
            {onboardingBalance?.map((v) => (
              <Tag
                key={v}
                size="large"
                onClick={() => setValue(v)}
                className={cls("funds-tag letter_spacing", {
                  active: v === value
                })}
              >
                ${acc.formatNumber(v, 0, ",", ".")}
              </Tag>
            ))}
          </div>

          <Divider style={{ flex: 1, minWidth: 20, margin: "14px 0" }} />

          <PaymentMethods
            paymentMethodsRef={paymentMethodsRef}
            bottomPadding={isMobile}
            isShopifyOnlyUser={isShopifyOnlyUser}
            isShopifyDefaultProcessor={isShopifyDefaultProcessor}
          />
          {!isMobile ? (
            <ButtonComponent
              type="primary"
              size="large"
              loading={saveFetching || fetching || addFundsLoading}
              onClick={handleSubmitForm}
              className={"flex center"}
              style={{ marginTop: 32, width: "100%" }}
              disabled={!value}
            >
              {t("Continue")}
            </ButtonComponent>
          ) : (
            <div className="pp-sticky-cta">
              <MobileCtaButtons isMobile={isMobile} onSaveForLater={handleNavigate} />
              <ButtonComponent
                type="primary"
                size="large"
                loading={saveFetching || fetching || addFundsLoading}
                onClick={handleSubmitForm}
                className={"flex center"}
                style={{ width: "100%" }}
                disabled={!value}
              >
                {t("Continue")}
              </ButtonComponent>
            </div>
          )}
        </Spin>
      </div>
    </Layout>
  )
}

export default OnboardingTopUp
